import { deserializeFacet, FacetsScope, FacetTypes, UNCLASSIFIED } from "./Facets";
import { isEqualBucketDefs } from "../schema/Attribute";
import { SelectionMode } from "../../wgs/scene/SelectionMode";
import { getRestrictedValueColor, PaletteGenerator, MultipleMepSystemsFacetColorId, MultipleRoomsFacetColorId } from "./ColorUtils";

const UNDEFINED = '(undefined)';

export class FacetsForScope {
  constructor(fm, scope, options, facetDefs) {
    this.fm = fm;
    this.scope = scope;
    this.facetsNeedRecalculation = true;
    this.themedFacet = undefined; // current themed facet def, used in theming.js

    this.facetDefs = facetDefs;
    this.facetDefs.forEach((fDef) => fDef.init({}, fm.facility, options));
    this.mergedFacets = this.facetDefs.map(() => []);
    this.emptyFacets = [];
  }

  isVisible(facetIdx, valueId) {
    const f = this.facetDefs[facetIdx].filter;
    return f.size === 0 || f.has(valueId);
  }

  getFacetDefIndex(facetType) {
    for (let i = 0; i < this.facetDefs.length; i++) {
      if (this.facetDefs[i].id === facetType) {
        return i;
      }
    }

    return -1;
  }

  getFacets() {
    return this.mergedFacets;
  }

  getFacetDefs() {
    return this.facetDefs;
  }

  getFacetDef(facetType) {
    const idx = this.getFacetDefIndex(facetType);
    if (idx === -1) {
      return null;
    }
    return this.facetDefs[idx];
  }

  getFacet(facetType) {
    const idx = this.getFacetDefIndex(facetType);
    if (idx === -1) {
      return null;
    }
    return this.mergedFacets[idx];
  }

  makeMergedFacets(idsByModel, ignoreFilter, skipFormat) {
    const mergedResult = [];
    const emptyFacets = [];
    const idsInMultipleFacets = [];
    const options = {
      useLastObject: this.fm.viewer.getSelectionMode() === SelectionMode.LAST_OBJECT,
      ignoreFilter,
      modelsByUrn: this.fm.models.reduce((dict, m) => {dict[m.urn()] = m;return dict;}, {}),
      allowEmptyFacets: this.scope !== FacetsScope.Context
    };
    for (let facetDef of this.facetDefs) {
      const res = facetDef.batchClassify(idsByModel, options);
      const facets = skipFormat ? res.facets : facetDef.formatMergedFacetNodes(res.facets);

      mergedResult.push(facets);
      idsByModel = res.idsByModelFiltered;
      idsInMultipleFacets.push(res.idsInMultipleFacetsByModel);
      if (!res.idsByModelFiltered.size) emptyFacets.push(facetDef);
    }

    return {
      mergedResult,
      idsByModelFiltered: idsByModel,
      idsInMultipleFacets,
      emptyFacets
    };
  }

  updateMergedFacets(idsByModel) {
    // TODO: still needed?
    this.facetDefs.forEach((f) => {
      if (f.filter.has(UNDEFINED)) {
        f.filter.delete(UNDEFINED);
        f.filter.add(undefined);
      }
    });

    const { mergedResult, idsByModelFiltered, idsInMultipleFacets, emptyFacets } = this.makeMergedFacets(idsByModel);
    this.mergedFacets = mergedResult;
    this.idsByModelFiltered = idsByModelFiltered;
    this.idsInMultipleFacets = idsInMultipleFacets;
    this.emptyFacets = emptyFacets;
  }

  async setSettings(newSettings) {
    if (!newSettings?.length) {
      return;
    }

    // Create current facet definitions maps. This will be used to restore state of existing facetdef when updating settings
    const existingFacetDefsById = {};
    for (let fd of this.facetDefs) {
      existingFacetDefsById[fd.id] = fd;
    }

    const newFacetDefs = [];

    //Enforce presence of facets which are required for parts of the UI to function,
    //but mark them as hidden, so they don't show in the Filters panel

    //Make sure the Source models facet exists and is first
    if (newSettings[0].id !== FacetTypes.models) {
      this.facetDefs[0].hidden = true;
    } else {
      this.facetDefs[0].hidden = newSettings[0].hidden;
    }
    newFacetDefs.push(this.facetDefs[0]);

    //Make sure the Levels facet exists
    const facetLevels = existingFacetDefsById[FacetTypes.levels];
    if (!facetLevels) {
      console.warn("unexpected situation -- levels facet does not exist");
    }
    let levelsIdx = newSettings.findIndex((fd) => fd.id === FacetTypes.levels);
    if (levelsIdx === -1) {
      levelsIdx = newFacetDefs.push(facetLevels) - 1;
      facetLevels.hidden = true;
    } else {
      facetLevels.hidden = newSettings[levelsIdx].hidden;
    }

    //Make sure the spaces facet exists -- there is a slight complication here in that
    //we add the spaces facet after the levels facet, which can be located anywhere in the input list
    const facetSpaces = existingFacetDefsById[FacetTypes.spaces];
    if (!facetSpaces) {
      console.warn("unexpected situation -- spaces facet does not exist");
    }
    let spacesIdx = newSettings.findIndex((fd) => fd.id === FacetTypes.spaces);
    if (spacesIdx === -1) {
      facetSpaces.hidden = true;
      //If we already added the Levels facet above, we can add spaces here,
      //otherwise we do it in the loop below
      if (levelsIdx === 1) {
        spacesIdx = newFacetDefs.push(facetSpaces) - 1;
      }
    } else {
      facetSpaces.hidden = newSettings[spacesIdx].hidden;
    }


    for (let i = 0; i < newSettings.length; i++) {

      const settings = newSettings[i];

      //Was already specially handled above
      if (settings.id === FacetTypes.models) continue;

      // reuse existing facet definitions to keep current state or instantiate new one
      let facetDef;
      const prevFacetDef = existingFacetDefsById[settings.id];
      const hasBuckets = prevFacetDef?.settings.buckets || settings.buckets;
      if (prevFacetDef && !hasBuckets && isEqualBucketDefs(prevFacetDef.settings.bucketDef, settings.bucketDef)) {
        facetDef = prevFacetDef;
      } else {
        facetDef = deserializeFacet(settings.id);
      }
      if (!facetDef) {
        continue;
      }

      await facetDef.init(settings, this.fm.facility, this.fm.options);

      newFacetDefs.push(facetDef);

      //Add the hidden Spaces facet after the Levels facet if that
      //wasn't already done above
      if (facetDef.id === FacetTypes.levels && spacesIdx === -1) {
        newFacetDefs.push(facetSpaces);
      }

    }

    //Check if facet defs are changing
    //If not, we can bypass facet recalculation.
    //TODO: we can do even better by only recalculating facets that are being added
    let facetsAreChanging = false;
    if (newFacetDefs.length !== this.facetDefs.length) {
      facetsAreChanging = true;
    }
    if (!facetsAreChanging) {
      for (let i = 0; i < this.facetDefs.length; i++) {
        if (this.facetDefs[i] !== newFacetDefs[i]) {
          facetsAreChanging = true;
          break;
        }
      }
    }

    if (this.facetsNeedRecalculation || facetsAreChanging) {
      this.facetsNeedRecalculation = false;

      const models = this.fm.models;
      if (models.length) {
        await this.updateFacetsAttributes(newFacetDefs, models);
        return this.fm.recalculateModelFacets([this.scope, newFacetDefs], models, true, true);
      } else {
        //No models in the scene, probably we are getting called
        //by view restoring code, before any model is loaded/added yet.
        //Just remember the new settings and let the first call to addModel()
        //do the recalculation.
        this.facetDefs = newFacetDefs;

        // sync merged facets by calling updateMergedFacets with no ids to match new facet defs
        this.updateMergedFacets(new Map());
      }
    }
  }

  /**
   *
   * @param {import("./Facets").FacetSettings} settings
   * @param {number} [insertIndex]
   * @returns
   */
  async addFacetDef(settings, insertIndex) {
    const nFacetDefs = this.facetDefs.slice();

    const facetDef = deserializeFacet(settings.id);
    if (!facetDef) {
      return nFacetDefs;
    }

    await facetDef.init(settings, this.fm.facility, this.fm.options);

    await this.updateFacetsAttributes([facetDef], this.fm.models);

    if (typeof insertIndex !== "number" || insertIndex < 0 || insertIndex > this.facetDefs.length) {
      const sysIndex = nFacetDefs.findIndex((v) => {
        return v.id === FacetTypes.tandemCategories;
      });
      if (sysIndex !== -1) {
        insertIndex = sysIndex + 1;
      } else {
        insertIndex = nFacetDefs.length;
      }
    }

    nFacetDefs.splice(insertIndex, 0, facetDef);

    await this.fm.recalculateModelFacets([this.scope, nFacetDefs], this.fm.models);

    return nFacetDefs;
  }

  /**
   * Map attributes from their hashes
   */
  async updateFacetsAttributes(facetDefs, models) {
    let hash2Attrs; // cross-model attributes map
    for (let i = 0; i < facetDefs.length; i++) {
      const facetDef = facetDefs[i];
      if (facetDef.settings.attributeHash) {
        //Collect attributes from all models
        if (!hash2Attrs) {
          hash2Attrs = {};
          const modelsHash2Attrs = await Promise.all(models.map((m) => m.getHash2Attr()));

          for (let i = 0; i < modelsHash2Attrs.length; i++) {
            for (let hash in modelsHash2Attrs[i]) {
              let attr = modelsHash2Attrs[i][hash];
              hash2Attrs[hash] = attr;
            }
          }
        }

        let attribute = hash2Attrs[facetDef.settings.attributeHash];
        if (attribute) {
          facetDef.setAttribute(attribute);
        } else {
          //Attempt to match using old (incorrect) attribute hash that is of the form [psetUuid][paramUuid][dataType]
          //instead of [paramUuid][dataType]. This should only happen when trying to load old saved views with custom facets
          let firstBrace = facetDef.settings.attributeHash.indexOf("]");
          if (firstBrace > 0) {
            attribute = hash2Attrs[facetDef.settings.attributeHash.slice(firstBrace + 1)];
            if (attribute) {
              console.log("Matched parameter using old hash function", facetDef.settings.attributeHash);
              facetDef.setAttribute(attribute);
            }
          }

        }
      }
    }
  }

  setFilterMap(filterMap, mergeIntoExisting, skipUpdate) {
    for (let def of this.facetDefs) {
      if (!mergeIntoExisting) {
        def.filter.clear();
      }
      filterMap[def.id]?.forEach((f) => def.addFilter(f));
    }

    if (!skipUpdate) {
      this.fm.updateFacets(null, null, null, this.scope);
    }
  }

  /**
   * Generates a map of facet IDs to their corresponding filter sets.
   *
   * @returns {Object.<string, Set>} An object mapping each facet ID to a Set of its filter values.
   */
  getFilterMap() {
    return this.facetDefs.reduce((a, c) => {
      const new_set = new Set([]);
      for (const f of c.filter) {
        if (f === undefined) {
          new_set.add(UNDEFINED);
        } else {
          new_set.add(f);
        }
      }
      a[c.id] = new_set;
      return a;
    }, {});
  }

  async recalcModelFacets(nFacetDefs, foundModels) {
    nFacetDefs = nFacetDefs || this.facetDefs;

    // prune attribute facets that point to an attribute that was hidden (e.g. due to a template update)
    nFacetDefs = nFacetDefs.filter((facetDef) => !facetDef.attribute || !facetDef.attribute.isHidden());

    // cache external dependencies of the classification process
    await Promise.all(nFacetDefs.map((facetDef) => facetDef.cacheExternalDataForModels(foundModels)));

    this.facetDefs = nFacetDefs;
  }

  /** Removes any filter applied for the facet passed in by ID and updates facets. */
  clearFilter(id) {
    this.getFacetDef(id)?.filter.clear();
  }

  /**
   * @deprecated Use setVisibilityById() instead
   */
  setVisibility(facetIdx, valueId, visible) {

    const { filter } = this.facetDefs[facetIdx];
    if (filter.has(valueId) === visible) return;

    if (visible) {
      filter.add(valueId);
    } else {
      filter.delete(valueId);
    }
  }

  setVisibilityById(facetId, valueId, visible) {
    this.setVisibility(this.getFacetDefIndex(facetId), valueId, visible);
  }

  generateColorMap(defaultColorMaps) {
    return buildColorMapForScope(this.facetDefs, this.mergedFacets, defaultColorMaps || {});
  }
}

function buildColorMapForScope(facetDefs, mergedFacets, defaultColorMaps) {
  for (let i = 0; i < mergedFacets.length; i++) {
    const facetDef = facetDefs[i];
    const { palette, paletteById } = facetDef;

    let topLevelPaletteClasses;
    if (paletteById) {
      topLevelPaletteClasses = Object.keys(paletteById).sort((a, b) => {
        if (a.length > b.length) {
          return -1;
        } else if (a.length < b.length) {
          return 1;
        }
        return a.localeCompare(b);
      });
    }

    const colorGen = new PaletteGenerator();

    if (!defaultColorMaps[facetDef.id]) {
      defaultColorMaps[facetDef.id] = {};
    }

    const colorMap = defaultColorMaps[facetDef.id];
    // The use of RGBA (# + 8 char) indicates user defined value
    const hasUserDefinition = Object.values(colorMap).some((v) => v.length === 9);

    const usedColors = {};
    const reuseColorsByPrefix = facetDef.id === FacetTypes.spaces;

    let j = 0;
    const q = [...mergedFacets[i]];

    while (q.length > 0) {
      const mergedFacetNode = q.shift();

      // skip virtual nodes
      // don't override values as they can be updated by the user
      if (!hasUserDefinition && colorMap[mergedFacetNode.id] === undefined) {
        let colorHex;

        let colorId;
        //Special case of rooms whose names are composed of Name + Number.
        //We achieve something close to color by function by ignoring the room number piece
        if (reuseColorsByPrefix) {
          colorId = mergedFacetNode.label;
          const idx = colorId.lastIndexOf(" ");
          if (idx > 0) {
            colorId = colorId.slice(0, idx);
          }
          colorHex = usedColors[colorId];
        }

        let colorFromParent = false;
        if (mergedFacetNode.parentColorHex) {
          colorHex = mergedFacetNode.parentColorHex;
          colorFromParent = true;
        }

        // respect hierarchical colors
        if ((!colorHex || colorFromParent) && paletteById) {
          if (facetDef.id === FacetTypes.mepSystems) {
            if (mergedFacetNode.glowEffectId === -1) {
              colorHex = paletteById[UNCLASSIFIED];
            } else {
              colorHex = paletteById[mergedFacetNode.glowEffectId];
            }
          } else if (facetDef.id === FacetTypes.systemClasses) {
            if (mergedFacetNode.id === -1) {
              colorHex = paletteById[UNCLASSIFIED];
            } else {
              colorHex = paletteById[1 << mergedFacetNode.id];
            }
          } else {
            for (let i = 0; i < topLevelPaletteClasses.length; i++) {
              if (mergedFacetNode.id.startsWith(topLevelPaletteClasses[i])) {
                colorHex = paletteById[topLevelPaletteClasses[i]];
                break;
              }
            }
          }
        } else if (!colorHex && facetDef.attribute?.allowedValues?.list) {
          const dataType = facetDef.attribute.dataType;
          const allowedList = facetDef.attribute.allowedValues.list;
          const displayOptions = facetDef.attribute.displayOptions;
          const value = mergedFacetNode.id;

          colorHex = getRestrictedValueColor(dataType, allowedList, displayOptions, value);

          if (!colorHex) continue; // no fill
          colorHex = palette[j];
        } else if (!colorHex) {
          colorHex = palette[j];
        }

        if (!colorHex) {
          // Palette is "consumed": generate differentiable colors
          colorHex = colorGen.next();
          if (colorId) {
            usedColors[colorId] = colorHex;
          }
        }
        colorMap[mergedFacetNode.id] = colorHex;
      }
      j++;
      if (mergedFacetNode.children) {
        for (const child of mergedFacetNode.children) {
          child.parentColorHex = mergedFacetNode.parentColorHex || colorMap[mergedFacetNode.id];
          q.push(child);
        }
      }
      const multipleFacetsColorId = facetDef.id === FacetTypes.mepSystems ? MultipleMepSystemsFacetColorId : MultipleRoomsFacetColorId;
      colorMap[multipleFacetsColorId] = '#5A5A5A';
    }
  }
  return defaultColorMaps;
}